import { useEffect, useState } from 'react';
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { useInlineImageZoom } from './useInlineImageZoom';

export type ComparePanelProps = {
    title: string;
    badge: string;
    variant: 'raw' | 'batch';
    imageUrl?: string;
    fileLabel?: string;
    emptyLabel: string;
    /** Ürün geçişi sırasında wave skeleton zorla */
    forceLoading?: boolean;
};

const WavePanelSkeleton = ({ isBatch }: { isBatch: boolean }) => (
    <Box className="boc-compare-skeleton" sx={{ width: '100%', height: '100%', p: 2.5, boxSizing: 'border-box' }}>
        <Skeleton
            variant="rounded"
            animation="wave"
            width="100%"
            height="100%"
            sx={{
                bgcolor: isBatch ? 'rgba(99, 102, 241, 0.14)' : 'rgba(148, 163, 184, 0.38)',
                '&::after': {
                    background: isBatch
                        ? 'linear-gradient(90deg, transparent, rgba(165, 180, 252, 0.5), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                },
            }}
        />
    </Box>
);

/** Panel içi yakınlaştırma (modal yok) — wheel + drag pan + butonlar. */
export const ComparePanel = ({
    title,
    badge,
    variant,
    imageUrl,
    fileLabel,
    emptyLabel,
    forceLoading = false,
}: ComparePanelProps) => {
    const { t } = useTranslation();
    const isBatch = variant === 'batch';
    const zoomApi = useInlineImageZoom(imageUrl);
    const [imageReady, setImageReady] = useState(false);

    useEffect(() => {
        setImageReady(false);
    }, [imageUrl]);

    const isLoading = forceLoading || Boolean(imageUrl && !imageReady);
    const showEmpty = !forceLoading && !imageUrl;
    const showImage = Boolean(imageUrl) && imageReady && !forceLoading;
    const controlsDisabled = isLoading || showEmpty;

    return (
        <Box className={`boc-compare-card ${isBatch ? 'boc-compare-card--batch' : 'boc-compare-card--raw'}`}>
            <Box className={`boc-compare-card__header ${isBatch ? 'boc-compare-card__header--batch' : ''}`}>
                <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                    <Box className={`boc-dot ${isBatch ? 'boc-dot--batch' : 'boc-dot--raw'}`} />
                    <Typography
                        className={`boc-compare-card__title ${isBatch ? 'boc-compare-card__title--batch' : ''}`}
                        noWrap
                    >
                        {title}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                    <Typography className="boc-zoom-percent">{Math.round(zoomApi.zoom * 100)}%</Typography>
                    <Tooltip title={t('studio.batchOutputControl.zoomIn')}>
                        <span>
                            <IconButton
                                size="small"
                                className="boc-zoom-btn"
                                disabled={!zoomApi.canZoomIn || controlsDisabled}
                                onClick={() => zoomApi.applyZoom(zoomApi.zoom + zoomApi.zoomStep)}
                            >
                                <ZoomInIcon fontSize="inherit" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('studio.batchOutputControl.zoomOut')}>
                        <span>
                            <IconButton
                                size="small"
                                className="boc-zoom-btn"
                                disabled={!zoomApi.canZoomOut || controlsDisabled}
                                onClick={() => zoomApi.applyZoom(zoomApi.zoom - zoomApi.zoomStep)}
                            >
                                <ZoomOutIcon fontSize="inherit" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('studio.batchOutputControl.zoomReset')}>
                        <span>
                            <IconButton
                                size="small"
                                className="boc-zoom-btn"
                                disabled={!zoomApi.canReset || controlsDisabled}
                                onClick={zoomApi.resetView}
                            >
                                <RestartAltIcon fontSize="inherit" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            <Box
                ref={zoomApi.viewportRef}
                className="boc-compare-card__viewport"
                sx={{ cursor: controlsDisabled ? 'default' : zoomApi.viewportCursor }}
                onMouseDown={controlsDisabled ? undefined : zoomApi.handleMouseDown}
                onMouseMove={controlsDisabled ? undefined : zoomApi.handleMouseMove}
                onMouseUp={controlsDisabled ? undefined : zoomApi.handleMouseUp}
                onMouseLeave={controlsDisabled ? undefined : zoomApi.handleMouseUp}
            >
                {isLoading ? <WavePanelSkeleton isBatch={isBatch} /> : null}

                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={fileLabel || badge}
                        draggable={false}
                        onLoad={() => setImageReady(true)}
                        onError={() => setImageReady(true)}
                        onDoubleClick={showImage ? zoomApi.handleDoubleClick : undefined}
                        sx={{
                            display: showImage ? 'block' : 'none',
                            maxWidth: '88%',
                            maxHeight: '88%',
                            objectFit: 'contain',
                            borderRadius: 1,
                            userSelect: 'none',
                            transform: `translate(${zoomApi.pan.x}px, ${zoomApi.pan.y}px) scale(${zoomApi.zoom})`,
                            transformOrigin: 'center center',
                            transition: zoomApi.isDragging ? 'none' : 'transform 0.15s ease',
                            boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
                        }}
                    />
                ) : null}

                {showImage ? (
                    <>
                        <Box
                            className={`boc-compare-card__corner-badge ${isBatch ? 'boc-compare-card__corner-badge--batch' : ''}`}
                        >
                            {badge}
                        </Box>
                        {fileLabel ? (
                            <Box
                                className={`boc-compare-card__file-badge ${isBatch ? 'boc-compare-card__file-badge--batch' : ''}`}
                            >
                                {fileLabel}
                            </Box>
                        ) : null}
                    </>
                ) : null}

                {showEmpty ? (
                    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} px={2}>
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            width="72%"
                            height={180}
                            sx={{ bgcolor: 'rgba(148, 163, 184, 0.28)' }}
                        />
                        <Typography className="boc-compare-card__empty">{emptyLabel}</Typography>
                    </Stack>
                ) : null}
            </Box>
        </Box>
    );
};
